import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Checkbox, Chip, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";
import InspectionGrid, { InspectionColumn } from "./InspectionGrid";
import { AnalysisService } from "../../services/analysis/AnalysisService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { translationService } from "../../services/translation/TranslationService";
import PipeDiagram from "./PipeDiagram";
import { REVIEWED_PIPE_COLOR, reviewedFrameSx, reviewedGridSx, ReviewedChip } from "./reviewedGraphStyle";
import AnalysisControls, {
  AnalysisDisplayOptions,
  defaultAnalysisDisplayOptions,
} from "./AnalysisControls";
import { mergeDetections } from "./detectionMerge";
import { activeIndexByTime, secondsToOcrTimestamp } from "./playbackSync";
import { formatDetectionTime } from "../../services/ocr/InspectionInfoStorageModel";
import DetectionLegend, { LegendCode } from "./DetectionLegend";
import { inspectionInfoStorage } from "../../services/ocr/InspectionInfoStorageModel";
import { AnalysisDetection, buildAnalysisDetections } from "./analysisDetections";
import "./Inspection.scss";

/**
 * Per-row sign-off, when the caller wants the graph itself to be where a damage is approved.
 *
 * Omitted — the standalone Video Analysis page — the grid stays read-only and grows no column,
 * so nothing about that page changes.
 */
export interface GraphApproval {
  /** Whether this row is on the approved list right now (pending edits included). */
  isApproved(row: AnalysisDetection): boolean;
  onToggle(row: AnalysisDetection, next: boolean): void;
  /** The row whose frame is being captured, so only that one shows a spinner. */
  pendingId?: string | null;
  disabled?: boolean;
}

interface AnalysisInspectionGraphProps {
  videoAnalysis: AnalysisService;
  /**
   * Rows to draw instead of the raw analysis output — the approved list, when the Video
   * Analysis tab's source selector is on it. Omitted, the graph builds its own rows, which
   * is what the standalone Video Analysis page still does.
   */
  detections?: AnalysisDetection[];
  /** Approve a damage from the graph. See {@link GraphApproval}. */
  approval?: GraphApproval;
}

const t = (key: string) => translationService.translate(key);

const formatTime = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

/**
 * Analysis-side "Inspection Graph": the same table + pipe + arrows layout as the
 * OCR Inspection Graph, but fed by the video-analysis predictions. Detections
 * are positioned by metre when distance data exists, otherwise by time. The
 * shared AnalysisControls (Overall Confidence / Show Characterizations / Merge
 * Near Regions) refine what is shown.
 */
const AnalysisInspectionGraph: React.FC<AnalysisInspectionGraphProps> = observer(
  ({ videoAnalysis, detections: providedDetections, approval }) => {
    const [options, setOptions] = useState<AnalysisDisplayOptions>(
      defaultAnalysisDisplayOptions
    );
    const [hidden, setHidden] = useState<Set<string>>(new Set());

    const distances = videoAnalysis.vaData?.distances ?? [];

    const detections = useMemo<AnalysisDetection[]>(
      () => providedDetections ?? buildAnalysisDetections(videoAnalysis),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        providedDetections,
        videoAnalysis.allPredictions,
        distances,
        videoAnalysis.hasRoiData,
        videoAnalysis.roiDetections,
        videoAnalysis.roiDetectionsAtMinConfidence,
        videoAnalysis.analysisConfidenceOption,
        inspectionInfoStorage.detections,
      ]
    );

    const byMetre =
      videoAnalysis.sectionLength > 0 ||
      distances.length > 0 ||
      detections.some((d) => d.meters > 0);
    const posOf = (d: AnalysisDetection) => (byMetre ? d.meters : d.timestamp);

    // Sort by position, then optionally merge same-code detections whose distance
    // spans overlap / sit within the threshold into one (peak accuracy wins).
    const rows = useMemo<AnalysisDetection[]>(() => {
      const sorted = [...detections].sort((a, b) => posOf(a) - posOf(b));
      if (!options.mergeNearRegions) return sorted;
      return mergeDetections(sorted, { gap: options.mergeDistance, byMetre });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detections, options.mergeNearRegions, options.mergeDistance, byMetre]);

    // Use reduce, not Math.max(...spread): spreading a very large rows array as call
    // arguments overflows the call stack (RangeError) and crashed the graph on big files.
    const pipeLengthValue = byMetre
      ? videoAnalysis.sectionLength ||
        rows.reduce((m, r) => Math.max(m, r.meters), 0) ||
        50
      : videoPlayerService.videoDuration ||
        rows.reduce((m, r) => Math.max(m, r.timestamp), 0) ||
        1;

    const colorOf = (videoAnalysis as any).mainCodeColors as Record<string, string>;
    const mainCodeColors = Object.entries(colorOf || {}).map(([mainCode, color]) => ({
      mainCode,
      color,
    }));

    // Legend = distinct codes present (coloured); per-code show/hide filter.
    const legendCodes: LegendCode[] = useMemo(() => {
      const seen = new Map<string, string>();
      rows.forEach((r) => {
        if (!seen.has(r.mainCode)) seen.set(r.mainCode, colorOf?.[r.mainCode] ?? "gray");
      });
      return Array.from(seen, ([mainCode, color]) => ({ mainCode, color }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, colorOf]);
    const toggle = (code: string) =>
      setHidden((prev) => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
      });

    // Visible rows drive the grid, the pipe AND the active-index, so they all agree.
    //
    // The approved state travels ON the row rather than being read from a closure inside
    // renderCell. The old MUI X grid re-rendered a cell only when its ROW changed, not when some
    // prop the render function closed over changed -- so a damage could land on the approved list
    // with its tick box still showing empty. The plain table redraws every render, but keeping
    // the state on the row means a memoised row can never go stale either.
    const visibleRows = useMemo(() => {
      const shown = rows.filter((r) => !hidden.has(r.mainCode));
      if (!approval) return shown;
      return shown.map((r) => ({ ...r, approved: approval.isApproved(r) }));
    }, [rows, hidden, approval]);
    const activeIndex = activeIndexByTime(
      visibleRows.map((r) => r.timestamp),
      videoPlayerService.videoProgress
    );

    const codeWithChars = (d: AnalysisDetection) =>
      options.showCharacterizations ? `${d.mainCode}${d.c1}${d.c2}` : d.mainCode;

    // A plain coded cell: the same width and behaviour for all eight of them.
    const codedColumn = (field: string, headerName: string, flex = 0.45): InspectionColumn<AnalysisDetection> => ({
      field,
      headerName,
      flex,
      minWidth: 50,
    });

    /**
     * The APPROVED list, drawn as the Inspection Insights graph draws it.
     *
     * Same columns in the same order — station, time, the observation text, then the coded
     * fields — because both tabs are showing one inspection and a reviewer moving between them
     * should be reading the same table. Confidence and provenance are deliberately absent here,
     * exactly as status / condition / risk are absent from the OCR graph: this table says what
     * the inspection FOUND, and who touched a row is shown on its card underneath, next to the
     * frame it was judged from.
     */
    const approvedColumns: InspectionColumn<AnalysisDetection>[] = [
      {
        field: "meters",
        headerName: t("ocrService.inspectionInfo.distance"),
        width: 90,
        renderCell: (params) => {
          const d = params.row as AnalysisDetection;
          return d.meters > 0 ? d.meters.toFixed(2) : "—";
        },
      },
      {
        field: "timestamp",
        headerName: t("ocrService.inspectionInfo.time"),
        width: 90,
        // Analysis rows carry seconds; the OCR graph formats a clock timestamp. Converted so
        // both tables state the time the same way.
        renderCell: (params) =>
          formatDetectionTime(secondsToOcrTimestamp((params.row as AnalysisDetection).timestamp)),
      },
      {
        field: "text",
        headerName: t("ocrService.inspectionInfo.text"),
        flex: 4,
        minWidth: 150,
        renderCell: (params) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              fontSize: "0.85rem",
              width: "100%",
              paddingTop: 4,
              paddingBottom: 4,
            }}
          >
            {(params.row as AnalysisDetection).text}
          </div>
        ),
      },
      {
        ...codedColumn("mainCode", "MC"),
        renderCell: (params) => {
          const d = params.row as AnalysisDetection;
          return (
            <span style={{ color: colorOf?.[d.mainCode] ?? "inherit", fontWeight: 600 }}>
              {d.mainCode}
            </span>
          );
        },
      },
      codedColumn("c1", "C1"),
      codedColumn("c2", "C2"),
      codedColumn("sa", "SA"),
      codedColumn("q1", "Q1"),
      codedColumn("q2", "Q2"),
      codedColumn("positionFrom", t("ocrService.inspectionInfo.positionFrom"), 0.6),
      codedColumn("positionTo", t("ocrService.inspectionInfo.positionTo"), 0.6),
    ];

    const machineColumns: InspectionColumn<AnalysisDetection>[] = [
      // Sign-off lives in the first column so the decision reads left to right: approve this
      // damage, then see what it is. Approving here is what puts the damage on the approved
      // list — and, on the Inspection Insights page, captures its frame — so the reviewer never
      // has to copy the whole analysis across just to keep a handful of findings.
      ...(approval
        ? [
            {
              field: "__approve",
              headerName: t("comparison.va-detections.approve-column"),
              width: 110,
              renderCell: (params: any) => {
                const d = params.row as AnalysisDetection;

                // The frame is grabbed from the video one row at a time, so only the row being
                // captured is blocked; every other row stays tickable while it runs.
                if (approval.pendingId === d.id) {
                  return <CircularProgress size={18} />;
                }

                return (
                  <Tooltip title={t("comparison.va-detections.approve-hint")}>
                    <Checkbox
                      size="small"
                      // Read off the row, so the box redraws when the list changes.
                      checked={(params.row as any).approved ?? false}
                      disabled={approval.disabled}
                      // The row click seeks the video; a tick must not also move the playhead.
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => approval.onToggle(d, event.target.checked)}
                    />
                  </Tooltip>
                );
              },
            } as InspectionColumn<AnalysisDetection>,
          ]
        : []),
      {
        field: "mainCode",
        headerName: t("ocrService.inspectionInfo.code"),
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          const d = params.row as AnalysisDetection;
          return (
            <span style={{ color: colorOf?.[d.mainCode] ?? "inherit", fontWeight: 600 }}>
              {codeWithChars(d)}
            </span>
          );
        },
      },
      {
        field: "meters",
        headerName: t("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          const d = params.row as AnalysisDetection;
          return d.meters > 0 ? `${d.meters.toFixed(2)} m` : "—";
        },
      },
      {
        field: "timestamp",
        headerName: t("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 80,
        renderCell: (params) => formatTime((params.row as AnalysisDetection).timestamp),
      },
      {
        field: "accuracy",
        headerName: t("comparison.confidence"),
        flex: 1,
        minWidth: 90,
        // Reviewer-added rows carry no model confidence, so "0%" would read as "the model was
        // certain this is nothing" rather than "the model never proposed this".
        renderCell: (params) => {
          const d = params.row as AnalysisDetection;
          return d.accuracy > 0 ? `${Math.round(d.accuracy * 100)}%` : "—";
        },
      },
    ];

    // Which table this is. Rows handed in ARE the approved list; rows built here are the model's
    // own output, and those two want different columns.
    const columns = providedDetections ? approvedColumns : machineColumns;
    // Fed rows means fed the approved list (see the prop), so this is the reviewed view.
    const reviewed = providedDetections !== undefined;

    if (!videoAnalysis.analysisSuccessful) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">{t("comparison.needsBoth")}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        <AnalysisControls
          videoAnalysis={videoAnalysis}
          options={options}
          onChange={setOptions}
          mergeUnit={byMetre ? "m" : "s"}
          hideConfidence={providedDetections !== undefined}
        />

        <DetectionLegend
          codes={legendCodes}
          hidden={hidden}
          onToggle={toggle}
          onShowAll={() => setHidden(new Set())}
        />

        <Box className="base-data-table" sx={reviewed ? reviewedFrameSx : undefined}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6">{t("ocrService.inspectionInfo.table-name")}</Typography>
          {reviewed && <ReviewedChip />}
        </Box>

          <Grid
            container
            direction="row"
            spacing={0}
            sx={{ position: "relative", overflow: "hidden", flexWrap: "nowrap", height: "100%" }}
          >
            <Grid item sm="auto" sx={{ position: "sticky", left: 0, zIndex: 1 }}>
              <PipeDiagram
                distance={visibleRows.map(posOf)}
                pipeLengthValue={pipeLengthValue}
                view="extracted"
                selectedIndex={null}
                activeIndex={activeIndex}
                mainCodes={visibleRows.map((r) => r.mainCode)}
                mainCodeColors={mainCodeColors}
                pipeColor={reviewed ? REVIEWED_PIPE_COLOR : undefined}
              />
            </Grid>

            <div style={{ display: "flex", flexGrow: 1, overflow: "auto", height: "100%" }}>
              <InspectionGrid
                rows={visibleRows}
                columns={columns}
                activeIndex={activeIndex}
                followActive={videoPlayerService.isVideoPlaying}
                onRowClick={(row) => videoPlayerService.jumpTo(row.timestamp)}
                sx={reviewed ? reviewedGridSx : undefined}
              />
            </div>
          </Grid>
        </Box>
      </Box>
    );
  }
);

export default AnalysisInspectionGraph;
